/**
 * **외부 채널 연동 화면 어휘·문구의 정본** — 의존성이 없는 순수 모듈이다.
 *
 * 서버(`GET /api/partner-links/**`)는 **어휘(`state`·`bizNoStatus`·`applyBlockedReason`)만** 내리고 한글 문구는
 * 프론트가 만든다 — 배달 메뉴 동기화(`deliveryMenuNotes.js`·규약 §9.2)와 같은 결이다. 그 매핑을 여기 한 곳에만
 * 둔다: 화면마다 적으면 같은 `state` 의 문구가 갈리고, 그것이 서버 응답에 한글을 넣지 않기로 한 이유를 무너뜨린다.
 *
 * 그래서 이 파일은 **아무것도 import 하지 않는다**(Vue·Element·`@/` 별칭 금지). 앱 번들 밖(node 내장 러너)에서
 * 그대로 읽혀야 하기 때문이고, 그 규칙이 깨지면 분리한 이유가 사라진다.
 *
 * **채널이 둘이 되면서 상태표·문구·차단 사유를 [CHANNEL_RULES] 로 접었다**(네이버 계획 §2.2). 종전에는 표가
 * 하나뿐이라 `partnerLinkChip` 이 **모든 채널에 사업자번호 게이트**를 적용했는데, 네이버 플레이스는 사업자번호와
 * 무관하다 — 채널이 자기 규칙을 갖게 하는 것이 그 갈래를 화면에 흩뿌리지 않는 유일한 방법이다.
 *
 * 계약 정본:
 * - KT: `ordern-workspace/docs/kt-callpop/kt-우리동네-연동-1차-구현계획-20260909.md` §1.2·§1.3·§1.4·§3.4
 * - 네이버: `ordern-workspace/docs/naver-place/네이버-플레이스-연동-1차-구현계획-20260915.md` §2.2·§4.5·§5.2
 */

/** 채널 코드 — 서버 `PartnerLinkView.links[].partner` 와 같은 문자열 */
export const PARTNER = Object.freeze({
  MYTOWN: 'MYTOWN',
  NAVER_PLACE: 'NAVER_PLACE',
})

/** 어드민 API base (§3.4). 경로 리터럴을 화면마다 적지 않는다 */
export const PARTNER_LINK_API = '/api/partner-links'

/** 채널별 경로 조각 (`…/stores/{storeCd}/{조각}/check|apply|link|unlink`, KT §3.4 · 네이버 §4.5) */
export const PARTNER_ACTION_PATH = Object.freeze({
  [PARTNER.MYTOWN]: 'mytown',
  [PARTNER.NAVER_PLACE]: 'naver-place',
})

/** 네이버 연동 위저드 라우트 (계획 §2.1 C) — 라우터 등록·POS 매직링크·패널 버튼이 같은 문자열을 본다 */
export const NAVER_PLACE_LINK_PATH = '/v/pos/naver-place-link'

/**
 * **콜팝 참여 축** — 서버 응답의 `participation` 은 `links[]` 의 채널 행이 **아니다**(B1 보고서 §2).
 *
 * 참여는 접수·통지 라이프사이클이 없는 **매장 속성**이라 채널 행과 축이 다르다. 그 사실을 화면이 잊으면
 * 「우리동네TV 입점 = 콜팝 참여」로 읽히는데, 둘은 서로를 함의하지 않는다(TV 미가입 매장도 참여할 수 있다).
 * 그래서 어휘·경로·chip 을 채널 표에 섞지 않고 이 축 하나로 따로 둔다.
 */
export const PARTICIPATION_STATE = Object.freeze({
  ACTIVE: 'ACTIVE',
  ENDED: 'ENDED',
  NONE: 'NONE',
})

/**
 * 참여 전이의 출처 어휘.
 *
 * **`AUTO` 는 B7-b 가 열었다**(서버 보고서 §3-4) — 매핑 직후와 일일 배치가 조건을 채운 매장을 스스로
 * 참여시킨다. 그 값이 표에 「-」로 보이면 「누가 켰는지 모른다」가 되므로 낱말을 여기 한 곳에 더한다.
 */
export const PARTICIPATION_SOURCE = Object.freeze({
  ADMIN: 'ADMIN',
  API: 'API',
  IMPORT: 'IMPORT',
  AUTO: 'AUTO',
})

/** 참여 경로 조각 (`…/stores/{storeCd}/callpop/join|end`) — [PARTNER_ACTION_PATH] 와 같은 자리의 값이다 */
export const PARTICIPATION_ACTION_PATH = 'callpop'

/**
 * 외부 채널·참여 축의 **액션 id** — 서버 `sys_action.action_id` 와 같은 문자열이다.
 *
 * 화면이 문자열 리터럴로 물으면 오타가 **조용한 버튼 소실**(그 계정에 없는 것으로 읽힘)이 되고, 반대로
 * 맞다고 믿고 부른 호출은 401 이 되어 세션을 끊는다. 어느 쪽도 화면에서 원인이 보이지 않는다.
 */
export const PARTNER_LINK_ACTION = Object.freeze({
  /** `GET /api/partner-links/**`(외부 채널 연동 상태 조회) — 없으면 「외부 채널 연동」 탭을 그리지도, 그 조회를 부르지도 않는다 */
  VIEW: 'PartnerLinkView',
  CALLPOP_JOIN: 'PartnerLinkCallpopJoin',
  CALLPOP_END: 'PartnerLinkCallpopEnd',
  /**
   * `POST /api/partner-links/stores/{storeCd}/mytown/check`(KT 우리동네 가입 여부 조회) — 콜팝 매장 목록의 「TV 조회」를 가른다.
   * 그 목록을 쓰는 콜팝 관리자·운영사·관리점은 매장 정보 화면이나 외부 채널 연동 탭을 못 열어 그 탭의 「조회」를 누를 곳이 없다.
   */
  MYTOWN_CHECK: 'PartnerLinkCheck',
})

/**
 * chip 을 그릴 때 쓰는 **가상 채널 코드** — 서버가 내리는 값이 아니다.
 *
 * 요약줄·패널이 같은 `PartnerLinkChip` 으로 참여 chip 을 그리려면 「무엇의 chip 인가」를 가리키는 이름이 하나
 * 필요하다. `PARTNER` 에 넣지 않는 이유: 그 객체의 값은 `links[]` 의 `partner` 문자열이고, 참여는 그 배열에
 * 없다 — 같은 상자에 두면 `partnerLinkOf` 가 영영 찾지 못할 코드를 채널처럼 찾게 된다.
 */
export const PARTICIPATION_CHIP_KEY = 'CALLPOP_PARTICIPATION'

/** 해지 사유 길이 상한 — 서버(`StorePartnerParticipation.REASON_MAX`)가 400 으로 막는 값과 같다 */
export const PARTICIPATION_REASON_MAX = 255

/**
 * 참여 등록·해지 요청의 config — **`logoutOn403:false` 하나만** 옵트아웃한다(§16.170).
 *
 * 권한 행(`callpop/join`·`callpop/end`)은 SQL 회차가 넣는다. 그 SQL 이 아직 없는 환경에서 403 이 오면 이 앱의
 * 기본값은 세션을 지우고 로그인으로 하드 이동인데, 그러면 **외부 채널 탭 자체를 열 수 없다**. 여기서 대체 동작은
 * 「버튼을 끄고 권한 없음이라고 말한다」([participationWriteResult] 의 `DENIED`)이므로 옵트아웃 조건을 만족한다.
 *
 * `showErrorMessage` 는 **끄지 않는다** — 409(이미 참여 중 / 참여 중이 아님)의 서버 한글 문장을 그대로
 * 띄우는 것이 이 버튼의 답이다(B1 보고서 §2 409).
 */
export const PARTICIPATION_WRITE_CONFIG = Object.freeze({ logoutOn403: false })

/** 참여 쓰기의 결과 갈래 — 화면이 상태 코드가 아니라 이 낱말로 갈린다 */
export const PARTICIPATION_WRITE_RESULT = Object.freeze({
  OK: 'OK',
  DENIED: 'DENIED',
  CONFLICT: 'CONFLICT',
  FAILED: 'FAILED',
})

/**
 * 배치 조회 한 요청의 상한 — 서버가 `storeCds` 를 100개까지만 받는다(§3.4). 두 곳에 같은 수가 있는 것은
 * 계약이지 중복이 아니다: 서버는 그것을 **강제**하고 화면은 보내기 전에 [chunkStoreCds] 로 **쪼갠다**.
 */
export const PARTNER_LINK_BATCH_MAX = 100

/**
 * 목록 열의 배치 조회는 **부가 조회**다 — 실패해도 화면은 「—」로 답한다(§1.1).
 *
 * - `logoutOn403:false` (§16.170) — 권한 SQL 미적용 환경의 403 이 세션을 끝내지 않는다.
 * - `showErrorMessage:false` (§16.175) — 목록 한 칸의 실패를 빨간 토스트로 말하지 않는다.
 *
 * 둘 다 **대체 동작이 있을 때만** 쓰는 옵트아웃이고, 여기서 대체 동작은 「—」다. 단건 조회·신청은 기본값을
 * 그대로 둔다(서버 한글 메시지를 인터셉터가 띄운다, §4.2).
 */
export const PARTNER_LINK_BATCH_CONFIG = Object.freeze({ logoutOn403: false, showErrorMessage: false })

/**
 * 목록 **행의 「조회」** config — 콜팝 매장 목록의 「TV 조회」가 쓴다.
 *
 * - `logoutOn403:false` (§16.170) — [PARTICIPATION_WRITE_CONFIG] 와 같은 이유다. 세 역할(콜팝 관리자·운영사·관리점)의 권한
 *   행은 SQL 회차가 넣고, 매장 범위는 서버(`AdminStoreScopeResolver`)가 가른다. 그 403 에 이 화면의 답은 인터셉터가 띄우는
 *   서버 문장이고 목록은 그대로 쓸 수 있다 — 세션을 끝내면 목록 자체를 다시 열어야 한다.
 * - `showLoading:false` — 전역 잠금 로딩(「처리중 입니다...」 화면 전체 덮개)을 켜지 않는다. KT 를 부르는 동안 잠그는 것은
 *   **누른 그 버튼 하나**다(버튼 로딩) — 다른 행의 조회·필터·페이지는 그대로 쓴다.
 * - `showErrorMessage` 는 **끄지 않는다** — 400(사업자번호·매장 ID 규격, KT 반려)·503(자격 미설정·KT 응답 없음)의 서버 한글
 *   문장을 그대로 띄우는 것이 이 버튼의 답이다. 쓰기라 실패해도 다시 보내지 않는다(`policy.retryDelay`).
 *
 * 매장 정보 패널의 「조회」는 기본값을 쓴다 — 그 화면은 전역 로딩이 진행을 보이고 패널 버튼 전체를 잠근다(`busy`).
 */
export const PARTNER_LINK_ROW_CHECK_CONFIG = Object.freeze({ logoutOn403: false, showLoading: false })

/** 상태 어휘 — 서버와 같은 문자열. KT 는 8종 전부, 네이버는 `SUBSCRIBED`·`TERMINATED`·`NOT_SUBSCRIBED` 만 쓴다 */
export const PARTNER_LINK_STATE = Object.freeze({
  BIZ_NO_INVALID: 'BIZ_NO_INVALID',
  SUBSCRIBED: 'SUBSCRIBED',
  RECEIVED: 'RECEIVED',
  REQUESTED: 'REQUESTED',
  REQUEST_FAILED: 'REQUEST_FAILED',
  TERMINATED: 'TERMINATED',
  NOT_SUBSCRIBED: 'NOT_SUBSCRIBED',
  UNCHECKED: 'UNCHECKED',
})

/** 사업자번호 판정 어휘 — 검증의 정본은 서버 `BizNoRules` 이고 화면은 결과만 읽는다(§4.2) */
export const BIZ_NO_STATUS = Object.freeze({
  VALID: 'VALID',
  INVALID: 'INVALID',
  EMPTY: 'EMPTY',
})

/** 쓰기를 막는 사유 — 상태가 아니라 **표시 보조 필드**다(§1.2). 있으면 신청·연동·해지 버튼이 꺼진다 */
export const APPLY_BLOCKED_REASON = Object.freeze({
  NOT_CONFIGURED: 'NOT_CONFIGURED',
  WRITE_DISABLED: 'WRITE_DISABLED',
  TEL_MISSING: 'TEL_MISSING',
})

/** 통지·판정의 출처 어휘 (`source`) */
export const PARTNER_LINK_SOURCE = Object.freeze({
  CHECK: 'CHECK',
  NOTIFY: 'NOTIFY',
  REQUEST: 'REQUEST',
})

/** 해지 사유 어휘 — 자체 해지(`AGENCY_REQUEST`)·조회 확인(`NOT_MAPPED`)·웹훅 4종 (네이버 계획 §2.2·§3.1) */
export const UNLINK_REASON = Object.freeze({
  AGENCY_REQUEST: 'AGENCY_REQUEST',
  OWNER_CHANGED: 'OWNER_CHANGED',
  REPLACED_BY_OTHER_AGENCY: 'REPLACED_BY_OTHER_AGENCY',
  BUSINESS_DELETED: 'BUSINESS_DELETED',
  ADMIN_FORCED: 'ADMIN_FORCED',
  NOT_MAPPED: 'NOT_MAPPED',
})

/**
 * 안내 불릿 안의 **자리표** — 「같은 사업자번호 N개 매장 공통」은 뷰에서 계산되는 문장이라(§1.4 2번) 값이 아니라
 * 자리로 적는다. 채널 규칙이 문장의 **순서**를 소유하고, [channelNotes] 가 그 자리에 값을 넣거나 줄을 버린다.
 */
export const SAME_BIZ_NO_NOTE = '__SAME_BIZ_NO_NOTE__'

/**
 * 화면 문구 — KT §1.4 가 「화면에 반드시 남길 것」으로 확정한 네 문장과 네이버 §5.2 세 문장이 여기 있다.
 *
 * 이 상수를 고치면 chip·패널·위저드·테스트가 함께 움직인다. 문장을 화면에 다시 타이핑하지 않는다.
 */
export const PARTNER_LINK_TEXT = Object.freeze({
  panelTitle: '외부 채널 연동',
  channelColumn: '채널',
  stateColumn: '상태',
  judgeColumn: '판정',
  receiptColumn: '접수번호 · 접수일',
  checkedAtColumn: '마지막 조회',
  eventDateColumn: '통지일',
  linkedAtColumn: '연동일 · 해지일',
  requestIdColumn: '요청 ID',
  failReasonColumn: '실패 사유',

  checkAction: '조회',
  /** 콜팝 매장 목록의 우리동네 「조회」 — 채널 칸이 없는 표라 무엇을 묻는지 앞에 단다(참여 칸의 「TV 입점」 줄과 같은 머리) */
  tvCheckAction: 'TV 조회',
  applyAction: '연동 신청',
  retryAction: '다시 시도',
  infoAction: '정보',
  copyAction: '복사',
  linkAction: '연동하기',
  unlinkAction: '연동 해지',

  noStore: '매장을 먼저 저장한 뒤 외부 채널 연동을 신청할 수 있습니다.',
  loadFailed: '외부 채널 연동 상태를 불러오지 못했습니다.',
  noLink: '연동 가능한 외부 채널이 없습니다.',
  requestIdCopied: '요청 ID를 복사했습니다.',
  copyFailed: '복사하지 못했습니다. 값을 직접 선택해 복사하세요.',
  wizardPopupBlocked: '새 창이 차단되었습니다. 브라우저의 팝업 차단을 해제한 뒤 다시 시도해 주세요.',

  applyConfirmTitle: '외부 채널 연동 신청',
  applyConfirmButton: '신청',
  applyCancelButton: '취소',
  /**
   * 보내는 항목과 동의를 한 문장에 — 매장 전화가 사장님 휴대폰이면 개인정보라 「보낸다」를 알리고 [신청]을 그 동의로 본다
   * (개인정보 보호법 §17 — 처리방침 개정 초안 검토 메모 E2, 10-24 사용자 결정).
   */
  applyConfirmTail:
    '이 값(매장명·사업자번호·콜백 전화)을 KT알파(지니TV 우리동네)에 보내 입점을 신청합니다 — [신청]을 누르면 보내는 데 동의한 것으로 봅니다. 접수 후 가입 확정까지 약 1일, TV 노출은 KT 내부 절차를 거칩니다.',
  applyCallbackTelLabel: '콜백 전화',
  applyCallbackTelMissing: '(없음 — 매장 전화번호를 먼저 등록)',

  // ── §1.4 문구 규칙 (화면에 반드시 남길 것) ────────────────────────────────────
  /** 1) 전송 완료 ≠ TV 노출 */
  notTvExposure: '전송이 완료되어도 TV 노출은 KT 내부 절차를 거칩니다 — 접수·가입 완료가 곧 TV 노출은 아닙니다.',
  /** 3) 익일 08~09시 배치 통지 */
  batchNotice: '가입 확정·해지는 익일 08~09시 KT 배치로 통지되어 이 화면에 반영됩니다.',
  /** 4) CS 경로 */
  csGuide: 'TV 게시·심사 결과는 오더엔 운영을 거쳐 KT 담당자에게 문의합니다. 문의 시 요청 ID를 함께 알려 주세요.',
  /** 4) 해지 안내 — 해지 API 는 규격에 없다 */
  terminateGuide: '해지는 KT(KOS) 절차로 진행되며, 처리되면 익일 이 화면에 반영됩니다.',

  placesTitle: '가입 플레이스',
  placesNote: '편성 변경·탈퇴는 플레이스 관리에서 합니다.',
  noPlaces: '편성된 플레이스가 없습니다.',

  bizNoFixGuide: '사업자정보 탭에서 사업자번호를 고친 뒤 조회하세요.',
  tvExposureGuide: 'TV 노출 여부는 KT 담당자 채널로 확인합니다.',
  receivedGuide: '가입 확정은 익일 08~09시 KT 배치로 통지됩니다.',
  requestedGuide: 'KT 담당자 확인 대기 중입니다. 접수가 끝날 때까지 다시 신청할 수 없습니다.',

  /**
   * 차단 문구는 **자기완결**이다 — 문장이 스스로 어느 채널의 일인지 말한다.
   *
   * 채널명을 그리는 쪽에서 앞에 붙이면 「KT 우리동네 · KT 우리동네 연동 자격이…」처럼 이름이 두 번 나온다
   * (compose 실측). 이름을 붙일지 말지를 문장마다 판단하는 분기를 두는 대신, 모든 문장이 같은 규칙을 지킨다.
   */
  blockedNotConfigured: 'KT 우리동네 연동 자격이 설정되지 않은 환경입니다.',
  blockedWriteDisabled: 'KT 우리동네 · 이 환경에서는 조회만 가능합니다.',
  blockedTelMissing: 'KT 우리동네 · 매장 전화번호가 없어 신청할 수 없습니다. 매장 정보에서 전화번호를 등록하세요.',
  /**
   * 모르는 사유의 **폴백** — 서버가 어휘를 늘렸을 때 버튼만 조용히 사라지지 않게 한다.
   *
   * 사유를 모르는 채로 「할 수 없다」고만 말하는 것은 빈약하지만, 아무 말 없이 버튼이 없는 화면보다는 낫다.
   * 채널명은 [partnerLinkChip] 이 앞에 붙여 다른 차단 문구와 같은 모양(자기완결)으로 만든다.
   */
  blockedUnknown: '지금은 이 작업을 할 수 없습니다.',

  subscribedYes: '가입',
  subscribedNo: '미가입',
  subscribedUnknown: '미확인',
  /**
   * 「조회」를 눌러 받은 판정이 N 일 때 띄우는 안내 — **KT 가 준 문안 그대로**다(2026-09-30 심홍섭, 대응방안 결정 40).
   * 입점 확인은 요청 액션(이 버튼)으로만 하므로 N 을 받은 사람에게 다음 행동을 말해 준다. 표의 판정 칸은
   * 여전히 [subscribedNo] 한 낱말이다 — 이 문장은 조회를 **요청한 순간**의 답이지 상태 표시가 아니다.
   */
  checkNotSubscribed: '우리동네TV 입점이 확인되지 않습니다. 관리점으로 문의해주세요',
  /** 판정 Y 의 완료 한 줄 — KT 문안([checkNotSubscribed])과 짝이다. 콜팝 매장 목록만 띄운다([partnerCheckReply]) */
  checkSubscribed: '우리동네TV 입점이 확인되었습니다.',
  sourceCheck: '조회',
  sourceNotify: 'KT 통지',
  sourceRequest: '신청',

  // ── 네이버 플레이스 (계획 §2.2·§5.2) ──────────────────────────────────────────
  /** §5.2 불릿 1 — SUBSCRIBED chip 의 부기도 같은 문장이다(문장을 두 번 쓰지 않는다) */
  naverBillAuto: '연동 후 POS 결제가 마감될 때 매출이 네이버로 전송됩니다.',
  /** §5.2 불릿 2 */
  naverUnlinkAuto:
    '연동 해지는 이 화면에서 직접 할 수 있으며, 네이버 쪽 사정(주인 권한 변경·타 POS 교체·업체 삭제·관리자 해지)으로 해지되면 자동으로 반영됩니다.',
  /** §5.2 불릿 3 */
  naverNoPersonalData: '네이버 계정·개인정보는 오더엔에 저장하지 않습니다.',
  naverRelinkGuide: '다시 연동하려면 「연동하기」로 네이버 로그인부터 진행합니다.',
  blockedNaverNotConfigured: '네이버 플레이스 연동 자격이 설정되지 않은 환경입니다.',
  blockedNaverWriteDisabled: '네이버 플레이스 · 이 환경에서는 조회만 가능합니다.',

  unlinkConfirmTitle: '네이버 플레이스 연동 해지',
  unlinkConfirmButton: '해지',
  unlinkCancelButton: '취소',
  unlinkConfirmTail:
    '네이버 플레이스 연동을 해지합니다. 해지하면 결제 마감 매출이 더 이상 네이버로 전송되지 않으며, 다시 연동하려면 네이버 로그인부터 진행해야 합니다.',

  placeIdLabel: '플레이스 ID',
  linkedAtLabel: '연동일',
  unlinkedAtLabel: '해지일',
  consentAtLabel: '동의',

  unlinkReasonAgencyRequest: '직접 해지',
  unlinkReasonOwnerChanged: '주인 권한 변경',
  unlinkReasonReplaced: '다른 POS 로 교체',
  unlinkReasonBusinessDeleted: '업체 삭제',
  unlinkReasonAdminForced: '네이버 관리자 해지',
  unlinkReasonNotMapped: '네이버에 연동 없음',

  // ── 콜팝 참여 (2차 계획 P1 · 09-14 밤 결정 1) ────────────────────────────────
  participationTitle: '콜팝 참여',
  participationLongName: '네이스 콜팝 참여',
  joinedAtColumn: '참여 시작',
  endedAtColumn: '종료',
  sourceColumn: '출처',

  joinAction: '참여 등록',
  endAction: '참여 해지',

  participationActive: '참여 중',
  participationEnded: '해지됨',
  participationNone: '미등록',

  /** 축이 다르다는 사실 — 이 화면에서 가장 자주 오해되는 지점이라 표 위에 한 줄로 못박는다 */
  participationAxisNote: '콜팝 참여는 우리동네TV 입점과 별개입니다.',
  participationSendingNote: '참여 매장의 콜팝 앱·웹 전화주문 거래가 네이스에 전달됩니다.',
  participationEndedNote: '해지 시점부터 거래가 전달되지 않습니다. 다시 등록하면 그때부터 다시 전달됩니다.',
  participationNoneNote: '아직 참여를 등록하지 않은 매장입니다.',

  participationSourceAdmin: '관리자 화면',
  participationSourceApi: '파트너 API',
  participationSourceImport: '일괄 이관',
  /** 매핑 직후·일일 배치가 스스로 켠 것(B7-b §3-4 `AUTO`) */
  participationSourceAuto: '자동 참여',

  joinConfirmTitle: '콜팝 참여 등록',
  joinConfirmButton: '등록',
  joinConfirmTail: '이 매장을 콜팝 참여 매장으로 등록합니다. 등록 시점부터 거래가 네이스에 전달됩니다.',

  endConfirmTitle: '콜팝 참여 해지',
  endConfirmButton: '해지',
  endConfirmTail:
    '참여를 해지하면 이 매장의 거래가 더 이상 네이스에 전달되지 않습니다. 해지 사유(선택)를 남길 수 있습니다.',
  endReasonPlaceholder: '해지 사유 (선택, 255자 이내)',
  endReasonTooLong: '해지 사유는 255자까지 입력할 수 있습니다.',

  /** 403 의 대체 동작 — 버튼을 없애지 않고 **끈 채로** 이유를 말한다 */
  participationDenied: '권한 없음',
  participationDeniedTip: '이 계정에는 콜팝 참여를 변경할 권한이 없습니다.',
})

const DISPLAY_NAME = Object.freeze({
  [PARTNER.MYTOWN]: 'KT 우리동네',
  [PARTNER.NAVER_PLACE]: '네이버 플레이스',
  [PARTICIPATION_CHIP_KEY]: PARTNER_LINK_TEXT.participationTitle,
})

const LONG_NAME = Object.freeze({
  [PARTNER.MYTOWN]: '지니TV 우리동네 (KT알파)',
  [PARTNER.NAVER_PLACE]: '네이버 스마트플레이스 플레이스 플러스',
  [PARTICIPATION_CHIP_KEY]: PARTNER_LINK_TEXT.participationLongName,
})

/** chip·표에 쓰는 짧은 채널명. 모르는 코드는 코드 그대로 — 화면이 빈칸이 되지 않는다 */
export function partnerDisplayName(partner) {
  if (!partner) return ''
  return DISPLAY_NAME[partner] || partner
}

/** 툴팁 첫 줄에 쓰는 정식 명칭(§1.2). 없으면 짧은 이름으로 떨어진다 */
export function partnerLongName(partner) {
  if (!partner) return ''
  return LONG_NAME[partner] || partnerDisplayName(partner)
}

/**
 * 한 채널의 **버튼 어휘** — 없는 버튼은 `false` 로 명시한다. 화면은 이 표에 있는 이름만 묻는다.
 *
 * `link`·`unlink` 는 네이버가 들여온 낱말이다: KT 의 `apply` 는 「접수를 보낸다」(비동기 통지)이고 네이버의
 * `link` 는 「위저드를 연다」(동기 연동)라 같은 버튼이 아니다.
 */
const NO_ACTIONS = Object.freeze({
  check: false,
  apply: false,
  retry: false,
  info: false,
  link: false,
  unlink: false,
  // 참여 축의 두 낱말도 같은 표에 산다 — 화면이 버튼을 물을 때 축마다 다른 모양을 묻게 하지 않는다
  join: false,
  end: false,
})

const actionsOf = (over) => Object.freeze({ ...NO_ACTIONS, ...over })

/**
 * KT 상태 → chip 표 (§1.2 그대로).
 *
 * `tagType` 의 `primary` 는 Element UI 에 정의된 type 이 아니라 **기본 톤(파랑)** 으로 떨어진다 —
 * `.el-tag` 기본 스타일이 파랑이고 `el-tag--primary` 규칙은 없다. 계획서의 어휘를 그대로 둔다.
 */
const MYTOWN_STATE_CHIP = Object.freeze({
  [PARTNER_LINK_STATE.BIZ_NO_INVALID]: {
    text: '사업자번호 확인 필요',
    tagType: 'warning',
    note: PARTNER_LINK_TEXT.bizNoFixGuide,
    actions: actionsOf({}),
  },
  [PARTNER_LINK_STATE.SUBSCRIBED]: {
    text: '연동',
    tagType: 'success',
    note: PARTNER_LINK_TEXT.tvExposureGuide,
    actions: actionsOf({ check: true, info: true }),
  },
  [PARTNER_LINK_STATE.RECEIVED]: {
    text: '접수 완료 (가입 대기)',
    tagType: 'primary',
    note: PARTNER_LINK_TEXT.receivedGuide,
    actions: actionsOf({ check: true }),
  },
  [PARTNER_LINK_STATE.REQUESTED]: {
    text: '접수 요청됨',
    tagType: 'primary',
    note: PARTNER_LINK_TEXT.requestedGuide,
    actions: actionsOf({ check: true }),
  },
  [PARTNER_LINK_STATE.REQUEST_FAILED]: {
    text: '전송 실패',
    tagType: 'danger',
    note: PARTNER_LINK_TEXT.csGuide,
    actions: actionsOf({ retry: true }),
  },
  [PARTNER_LINK_STATE.TERMINATED]: {
    text: '해지됨',
    tagType: 'info',
    note: PARTNER_LINK_TEXT.terminateGuide,
    actions: actionsOf({ check: true, apply: true }),
  },
  [PARTNER_LINK_STATE.NOT_SUBSCRIBED]: {
    text: '미연동',
    tagType: 'info',
    note: null,
    actions: actionsOf({ check: true, apply: true }),
  },
  [PARTNER_LINK_STATE.UNCHECKED]: {
    text: '미확인',
    tagType: 'info',
    note: null,
    actions: actionsOf({ check: true, apply: true }),
  },
})

/**
 * 네이버 상태 → chip 표 (네이버 계획 §2.2 표 그대로). 세 상태뿐이고 `UNCHECKED`·모르는 값은 `NOT_SUBSCRIBED` 다 —
 * 연동 API 가 **동기 응답**이라 「요청했지만 아직 모른다」는 국면이 없기 때문이다(§3.1).
 */
const NAVER_PLACE_STATE_CHIP = Object.freeze({
  [PARTNER_LINK_STATE.SUBSCRIBED]: {
    text: '연동',
    tagType: 'success',
    note: PARTNER_LINK_TEXT.naverBillAuto,
    actions: actionsOf({ check: true, unlink: true, info: true }),
  },
  [PARTNER_LINK_STATE.TERMINATED]: {
    text: '해지됨',
    tagType: 'info',
    note: PARTNER_LINK_TEXT.naverRelinkGuide,
    actions: actionsOf({ check: true, link: true, info: true }),
  },
  [PARTNER_LINK_STATE.NOT_SUBSCRIBED]: {
    text: '미연동',
    tagType: 'info',
    note: null,
    actions: actionsOf({ link: true }),
  },
})

const MYTOWN_BLOCKED_TEXT = Object.freeze({
  [APPLY_BLOCKED_REASON.NOT_CONFIGURED]: PARTNER_LINK_TEXT.blockedNotConfigured,
  [APPLY_BLOCKED_REASON.WRITE_DISABLED]: PARTNER_LINK_TEXT.blockedWriteDisabled,
  [APPLY_BLOCKED_REASON.TEL_MISSING]: PARTNER_LINK_TEXT.blockedTelMissing,
})

const NAVER_PLACE_BLOCKED_TEXT = Object.freeze({
  [APPLY_BLOCKED_REASON.NOT_CONFIGURED]: PARTNER_LINK_TEXT.blockedNaverNotConfigured,
  [APPLY_BLOCKED_REASON.WRITE_DISABLED]: PARTNER_LINK_TEXT.blockedNaverWriteDisabled,
})

/**
 * **채널 규칙** — 상태표·차단 문구·안내 불릿·사업자번호 게이트를 채널이 소유한다(네이버 계획 §2.2).
 *
 * `requiresBizNo` 가 표의 맨 위 판정을 켠다. KT 는 사업자번호가 신청의 입자라 켜져 있고(§2.1), 네이버는
 * 네아로 계정의 **주인 권한**으로 판정하므로 꺼져 있다 — 사업자번호가 비어 있어도 네이버 연동은 성립한다.
 */
export const CHANNEL_RULES = Object.freeze({
  [PARTNER.MYTOWN]: Object.freeze({
    requiresBizNo: true,
    stateChip: MYTOWN_STATE_CHIP,
    fallbackState: PARTNER_LINK_STATE.UNCHECKED,
    blockedText: MYTOWN_BLOCKED_TEXT,
    /** 표의 「판정」 칸(`subscribedYn`·출처)이 있는 채널인가 — KT 가 조회·통지로 주는 가입 여부다([partnerJudgeText]) */
    hasJudgement: true,
    /** 조회 직후의 판정(`subscribedYn`)별 안내 — [partnerCheckNotice] 가 읽는다 */
    checkNotice: Object.freeze({ N: PARTNER_LINK_TEXT.checkNotSubscribed }),
    /** 안내가 없는 판정의 완료 한 줄 — [partnerCheckReply] 만 읽는다(매장 정보 패널은 쓰지 않는다 — 그 함수 주석) */
    checkDone: Object.freeze({ Y: PARTNER_LINK_TEXT.checkSubscribed }),
    notes: Object.freeze([
      PARTNER_LINK_TEXT.notTvExposure,
      SAME_BIZ_NO_NOTE,
      PARTNER_LINK_TEXT.batchNotice,
      PARTNER_LINK_TEXT.csGuide,
      PARTNER_LINK_TEXT.terminateGuide,
    ]),
  }),
  [PARTNER.NAVER_PLACE]: Object.freeze({
    requiresBizNo: false,
    stateChip: NAVER_PLACE_STATE_CHIP,
    fallbackState: PARTNER_LINK_STATE.NOT_SUBSCRIBED,
    blockedText: NAVER_PLACE_BLOCKED_TEXT,
    /** 네이버는 `subscribedYn` 을 채우지 않는다 — 연동 여부는 상태 칸(chip)이 정본이다 */
    hasJudgement: false,
    checkNotice: Object.freeze({}),
    checkDone: Object.freeze({}),
    notes: Object.freeze([
      PARTNER_LINK_TEXT.naverBillAuto,
      PARTNER_LINK_TEXT.naverUnlinkAuto,
      PARTNER_LINK_TEXT.naverNoPersonalData,
    ]),
  }),
})

/**
 * 모르는 채널의 규칙 — KT 표로 떨어진다. 종전(표가 하나였을 때)의 동작을 그대로 지키는 자리다:
 * 서버가 새 채널을 먼저 내려도 화면이 빈칸이 되지 않고 「미확인 · 조회·신청」으로 보인다.
 */
export function channelRulesOf(partner) {
  return CHANNEL_RULES[partner] || CHANNEL_RULES[PARTNER.MYTOWN]
}

/**
 * 참여 상태 → chip 표. 채널 표와 **따로 있는 이유**는 [PARTICIPATION_STATE] 의 주석 그대로다 —
 * 상태 어휘도 버튼도 겹치지 않는다(참여에는 조회·접수·통지가 없고, 채널에는 등록·해지가 없다).
 */
const PARTICIPATION_CHIP = Object.freeze({
  [PARTICIPATION_STATE.ACTIVE]: {
    text: PARTNER_LINK_TEXT.participationActive,
    tagType: 'success',
    note: PARTNER_LINK_TEXT.participationSendingNote,
    actions: actionsOf({ end: true }),
  },
  [PARTICIPATION_STATE.ENDED]: {
    text: PARTNER_LINK_TEXT.participationEnded,
    tagType: 'info',
    note: PARTNER_LINK_TEXT.participationEndedNote,
    actions: actionsOf({ join: true }),
  },
  [PARTICIPATION_STATE.NONE]: {
    text: PARTNER_LINK_TEXT.participationNone,
    tagType: 'info',
    note: PARTNER_LINK_TEXT.participationNoneNote,
    actions: actionsOf({ join: true }),
  },
})

/**
 * **참여 축의 chip** — [partnerLinkChip] 과 같은 모양(`text`·`tagType`·`note`·`actions`)을 돌려준다. 순수 함수.
 *
 * 같은 모양인 것이 중요하다: `PartnerLinkChip` 과 패널의 버튼 열이 축을 몰라도 그릴 수 있고, 축이 늘어도
 * 그리는 쪽에 분기가 생기지 않는다. 모르는 상태·`null` 은 `NONE` 으로 떨어진다 — 서버가 어휘를 늘려도
 * 화면이 빈칸이 되지 않는다.
 *
 * @param {?object} participation `PartnerLinkView.participation`
 */
export function participationChip(participation) {
  const state = (participation && participation.state) || PARTICIPATION_STATE.NONE
  const base = PARTICIPATION_CHIP[state] || PARTICIPATION_CHIP[PARTICIPATION_STATE.NONE]
  return {
    text: base.text,
    tagType: base.tagType,
    note: base.note,
    actions: base.actions,
    applyBlockedText: null,
  }
}

/** 참여 출처 낱말. 모르는 값·없는 값은 빈 문자열이라 화면이 괄호만 남기지 않는다([partnerSourceText] 와 같은 규칙) */
export function participationSourceText(source) {
  if (source === PARTICIPATION_SOURCE.ADMIN) return PARTNER_LINK_TEXT.participationSourceAdmin
  if (source === PARTICIPATION_SOURCE.API) return PARTNER_LINK_TEXT.participationSourceApi
  if (source === PARTICIPATION_SOURCE.IMPORT) return PARTNER_LINK_TEXT.participationSourceImport
  if (source === PARTICIPATION_SOURCE.AUTO) return PARTNER_LINK_TEXT.participationSourceAuto
  return ''
}

/**
 * 해지 요청 본문 — 사유는 **선택**이라 비었으면 키 자체를 넣지 않는다(서버는 본문 생략도 받는다).
 * 빈 문자열을 보내면 「사유가 있다」는 뜻이 되므로, 있음/없음의 판정을 화면이 서버에 떠넘기지 않는다.
 */
export function participationEndBody(reason) {
  const clean = typeof reason === 'string' ? reason.trim() : ''
  return clean ? { reason: clean } : {}
}

/**
 * 참여 쓰기의 **결과 갈래** — 상태 코드를 화면이 직접 읽지 않게 한 곳에서 낱말로 바꾼다.
 *
 * - 실패 없음 → `OK`
 * - `403` → `DENIED`: 이 계정은 그 매장의 참여를 바꿀 수 없다. 버튼을 끄고 이유를 말한다(세션은 끝내지 않는다).
 * - `409` → `CONFLICT`: 서버가 보는 상태가 화면과 다르다. 서버 말이 맞으므로 **다시 읽는다**(문구는 인터셉터가 띄웠다).
 * - 그 밖 → `FAILED`: 인터셉터가 이미 말했고 화면은 그대로 둔다.
 *
 * 인자가 **상태 코드가 아니라 실패 자체**인 이유: 응답이 없는 실패(오프라인·타임아웃)는 상태 코드가 없어
 * `null` 이 되는데, 성공도 「코드 없음」이라 둘이 같은 값으로 접혔다 — 전송 실패가 `OK` 로 읽혔다.
 * 실패의 유무는 값의 유무로, 상태 코드는 그 안의 필드로 가른다.
 *
 * @param {?{status: ?number}} failure 실패(`{ status }`) 또는 `null`(성공)
 */
export function participationWriteResult(failure) {
  if (!failure) return PARTICIPATION_WRITE_RESULT.OK
  if (failure.status === 403) return PARTICIPATION_WRITE_RESULT.DENIED
  if (failure.status === 409) return PARTICIPATION_WRITE_RESULT.CONFLICT
  return PARTICIPATION_WRITE_RESULT.FAILED
}

/**
 * **한 채널의 chip** — 문구·톤·버튼을 한 번에 만든다(§1.2 · 네이버 §2.2). 순수 함수.
 *
 * `text` 는 **상태 낱말만**이고 채널명은 그리는 쪽이 앞에 붙인다(`네이버 플레이스 · 미연동`) — `DeliveryMenuChip`
 * 과 같은 골격이라 목록 칸과 패널이 같은 조각을 다르게 배치할 수 있다.
 *
 * `bizNoStatus` 를 함께 받는 이유: KT 는 사업자번호 판정이 표의 **맨 위**다. 서버가 이미 `BIZ_NO_INVALID` 를
 * 내리지만, 사업자번호를 고치는 중(같은 화면의 다른 탭)에는 뷰가 낡을 수 있고 그때 「연동 신청」이 살아 있으면
 * 안 된다. **그 게이트는 `requiresBizNo` 채널에만** 걸린다 — 네이버 연동은 사업자번호를 보지 않는다.
 *
 * @param {?object} link `PartnerLinkView.links[]` 한 건. `null` 이면 아직 조회된 적 없는 채널로 본다
 * @param {?string} bizNoStatus `PartnerLinkView.bizNoStatus`
 * @returns {{text: string, tagType: string, note: ?string, actions: object, applyBlockedText: ?string}}
 */
export function partnerLinkChip(link, bizNoStatus) {
  const rules = channelRulesOf(link && link.partner)
  const bizNoBroken = rules.requiresBizNo && bizNoStatus != null && bizNoStatus !== BIZ_NO_STATUS.VALID
  const state = bizNoBroken ? PARTNER_LINK_STATE.BIZ_NO_INVALID : (link && link.state) || rules.fallbackState
  const base = rules.stateChip[state] || rules.stateChip[rules.fallbackState]

  // 모르는 사유도 **말은 한다** — 버튼이 사라진 이유를 화면이 침묵으로 두지 않는다(폴백은 채널명을 앞에 붙여
  // 다른 차단 문구와 같은 자기완결 모양이 된다)
  const blockedReason = (link && link.applyBlockedReason) || null
  const applyBlockedText = blockedReason
    ? rules.blockedText[blockedReason] ||
      `${partnerDisplayName(link && link.partner)} · ${PARTNER_LINK_TEXT.blockedUnknown}`
    : null

  // 막힌 사유가 있으면 **쓰기 버튼만** 끈다 — 조회·정보는 여전히 답을 줄 수 있는 질문이다(§1.2 보조 필드)
  const actions = blockedReason
    ? { ...base.actions, apply: false, retry: false, link: false, unlink: false }
    : base.actions

  return {
    text: base.text,
    tagType: base.tagType,
    note: base.note,
    actions: Object.freeze({ ...actions }),
    applyBlockedText,
  }
}

/** 뷰에서 한 채널의 링크를 고른다 — 없으면 `null`(= 아직 조회된 적 없음) */
export function partnerLinkOf(view, partner) {
  if (!view || !Array.isArray(view.links)) return null
  return view.links.find((link) => link && link.partner === partner) || null
}

/**
 * 뷰의 참여 행 — 필드가 아예 없으면 `null` 이다.
 *
 * **없음(`null`)과 미등록(`state: 'NONE'`)은 다르다**: 앞은 「이 서버는 참여 축을 모른다」(참여 회차 배포 전)이고
 * 뒤는 「이 매장은 아직 등록하지 않았다」다. 배포 순서가 SQL → appserver → admin-web 이므로 화면이 서버보다
 * 먼저 갈 수 있고, 그때 참여 행을 그리면 없는 축을 있다고 말하게 된다.
 */
export function participationOf(view) {
  return (view && view.participation) || null
}

/**
 * chip 하나를 만드는 **입구** — 채널이든 참여든 같은 질문(`view`, 코드)으로 답한다.
 *
 * `PartnerLinkChip` 이 축마다 다른 함수를 부르면 chip 을 쓰는 화면마다 그 분기가 복제된다. 분기는 여기 한 번이다.
 */
export function partnerChipOf(view, partner) {
  if (partner === PARTICIPATION_CHIP_KEY) return participationChip(participationOf(view))
  return partnerLinkChip(partnerLinkOf(view, partner), view ? view.bizNoStatus : null)
}

/**
 * 요약줄이 그릴 chip 의 **코드 목록** — 참여 축이 먼저고 그다음이 서버가 내린 채널 순서다.
 *
 * 참여를 앞에 두는 이유: 그것이 거래 전달 여부를 정하는 축이고, TV 입점과 헷갈리는 쪽도 그것이다.
 * 참여 행이 없는 응답(구 서버)은 그 자리가 통째로 빠진다 — [participationOf] 주석 참고.
 */
export function partnerChipKeys(view) {
  const links = view && Array.isArray(view.links) ? view.links : []
  const channels = links.filter((link) => link && link.partner).map((link) => link.partner)
  return participationOf(view) ? [PARTICIPATION_CHIP_KEY, ...channels] : channels
}

/**
 * 쓰기 경로 한 곳 — `…/stores/{storeCd}/{조각}/{동작}` 의 모양을 화면마다 적지 않는다.
 * 채널(`mytown`·`naver-place`)과 참여(`callpop`)가 같은 base 아래 같은 모양이라 한 함수가 답한다.
 */
export function partnerActionUrl(storeCd, segment, action) {
  return `${PARTNER_LINK_API}/stores/${storeCd}/${segment}/${action}`
}

/**
 * 「조회」 응답 뷰에서 그 채널의 **안내 한 문장** — 없으면 `null`(아무것도 띄우지 않는다).
 *
 * [channelRulesOf] 를 쓰지 않는다: 그 함수는 모르는 채널을 KT 표로 떨어뜨리는데, 여기서 그러면 서버가 먼저
 * 내린 새 채널의 조회 결과에 KT 문안이 뜬다. 안내는 규칙이 있는 채널만 말한다.
 *
 * @param {?object} view 조회 응답(`PartnerLinkView`)
 * @param {string} partner 조회한 채널 코드
 * @returns {?string}
 */
export function partnerCheckNotice(view, partner) {
  const rules = CHANNEL_RULES[partner]
  const link = partnerLinkOf(view, partner)
  if (!rules || !link) return null
  return rules.checkNotice[link.subscribedYn] || null
}

/**
 * 「조회」를 **누른 사람에게 줄 답 한 줄** — `{ type, text }`(`$message` 의 톤·문장) 또는 `null`. 콜팝 매장 목록의 「TV 조회」가 쓴다.
 *
 * - 안내([partnerCheckNotice] — 판정 N 이면 KT 문안)가 있으면 그것이 답이고 경고 톤이다. 문장과 조건은 그 함수가 소유한다 —
 *   매장 정보 패널과 같은 판정에 같은 문장이 뜬다.
 * - 안내가 없으면 채널의 완료 한 줄(`checkDone` — KT 판정 Y)이고 성공 톤이다.
 * - 둘 다 없으면 `null` — 다시 읽은 목록의 「TV 입점」 줄이 답이다. 규칙이 없는 채널은 말하지 않는다([partnerCheckNotice] 와 같은 이유).
 *
 * 완료 한 줄을 패널은 쓰지 않는다: 그 표는 「판정」·「마지막 조회」 칸이 바로 옆에서 바뀐다(e2e `callpop-participation`
 * 「판정 Y 이면 아무것도 띄우지 않는다」). 목록은 조회 시각을 그리지 않아 판정이 그대로면(가입 → 가입) 다시 읽어도 화면이
 * 바뀌지 않는다 — 누른 사람에게 끝났다고 따로 말한다.
 *
 * @param {?object} view 조회 응답(`PartnerLinkView`)
 * @param {string} partner 조회한 채널 코드
 * @returns {?{type: string, text: string}}
 */
export function partnerCheckReply(view, partner) {
  const notice = partnerCheckNotice(view, partner)
  if (notice) return Object.freeze({ type: 'warning', text: notice })

  const rules = CHANNEL_RULES[partner]
  const link = partnerLinkOf(view, partner)
  const done = rules && link ? rules.checkDone[link.subscribedYn] : null
  return done ? Object.freeze({ type: 'success', text: done }) : null
}

/** 판정 낱말 — `subscribedYn` 은 `'Y'`·`'N'`·`null` 셋뿐이다 */
export function partnerSubscribedText(subscribedYn) {
  if (subscribedYn === 'Y') return PARTNER_LINK_TEXT.subscribedYes
  if (subscribedYn === 'N') return PARTNER_LINK_TEXT.subscribedNo
  return PARTNER_LINK_TEXT.subscribedUnknown
}

/** 판정의 출처 낱말. 모르는 값·없는 값은 빈 문자열이라 화면이 괄호만 남기지 않는다 */
export function partnerSourceText(source) {
  if (source === PARTNER_LINK_SOURCE.CHECK) return PARTNER_LINK_TEXT.sourceCheck
  if (source === PARTNER_LINK_SOURCE.NOTIFY) return PARTNER_LINK_TEXT.sourceNotify
  if (source === PARTNER_LINK_SOURCE.REQUEST) return PARTNER_LINK_TEXT.sourceRequest
  return ''
}

/**
 * 표 「판정」 칸 한 행 — `{ subscribedText, sourceText }`. 판정은 KT 가 조회·통지로 주는 가입 여부라 그 칸이 있는
 * 채널(`hasJudgement`)만 [partnerSubscribedText]·[partnerSourceText] 로 그린다. 네이버는 `subscribedYn` 을 채우지 않아
 * 그리면 연동 중에도 늘 「미확인」이고, 웹훅 해지(`source=NOTIFY`)에는 「KT 통지」가 붙는다 — 그런 채널은 `-` 하나다.
 * 모르는 채널은 [channelRulesOf] 의 KT 규칙을 따라 지금처럼 그린다.
 */
export function partnerJudgeText(link) {
  if (!channelRulesOf(link && link.partner).hasJudgement) return Object.freeze({ subscribedText: '-', sourceText: '' })
  return Object.freeze({
    subscribedText: partnerSubscribedText(link ? link.subscribedYn : null),
    sourceText: partnerSourceText(link ? link.source : null),
  })
}

const UNLINK_REASON_TEXT = Object.freeze({
  [UNLINK_REASON.AGENCY_REQUEST]: PARTNER_LINK_TEXT.unlinkReasonAgencyRequest,
  [UNLINK_REASON.OWNER_CHANGED]: PARTNER_LINK_TEXT.unlinkReasonOwnerChanged,
  [UNLINK_REASON.REPLACED_BY_OTHER_AGENCY]: PARTNER_LINK_TEXT.unlinkReasonReplaced,
  [UNLINK_REASON.BUSINESS_DELETED]: PARTNER_LINK_TEXT.unlinkReasonBusinessDeleted,
  [UNLINK_REASON.ADMIN_FORCED]: PARTNER_LINK_TEXT.unlinkReasonAdminForced,
  [UNLINK_REASON.NOT_MAPPED]: PARTNER_LINK_TEXT.unlinkReasonNotMapped,
})

/** 해지 사유 낱말. 모르는 코드는 **코드 그대로** — 새 사유가 생겨도 화면이 사실을 감추지 않는다 */
export function unlinkReasonText(reason) {
  if (!reason) return ''
  return UNLINK_REASON_TEXT[reason] || reason
}

/**
 * §1.4 2) 「같은 사업자번호 N개 매장 공통」 — 판정의 입자가 매장이 아니라 사업자번호라는 사실(§2.1).
 *
 * 배치 조회는 `sameBizNoStoreCount` 를 내리지 않으므로(`null`) 그때는 이 문장을 만들지 않는다.
 */
export function sameBizNoNote(sameBizNoStoreCount) {
  if (sameBizNoStoreCount == null || sameBizNoStoreCount < 1) return null
  return `이 판정은 같은 사업자번호로 등록된 매장 ${sameBizNoStoreCount}곳에 공통으로 적용됩니다.`
}

/**
 * 한 채널의 **안내 불릿** — 순서는 채널 규칙이 소유하고, 자리표([SAME_BIZ_NO_NOTE])만 뷰의 값으로 채운다.
 * 채울 값이 없으면 그 줄은 사라진다(배치 응답에는 `sameBizNoStoreCount` 가 없다).
 */
export function channelNotes(partner, view) {
  const rules = channelRulesOf(partner)
  const dynamic = rules.requiresBizNo ? sameBizNoNote(view ? view.sameBizNoStoreCount : null) : null
  return rules.notes.map((note) => (note === SAME_BIZ_NO_NOTE ? dynamic : note)).filter((note) => !!note)
}

/**
 * §1.3 1번 확인 문안 — 신청 전에 사장님이 확인하는 값 그대로: 매장명 · 사업자번호(정제값) · **콜백 전화(정제값)**.
 * 콜백 전화는 서버가 `PartnerLinkView.callbackTel` 로 내리는 값(§4.2 에 실리는 바로 그 값)이다 — 화면이 다시 계산하지 않는다.
 *
 * 줄바꿈을 넣지 않는 이유: Element 의 `$confirm` 은 본문을 한 문단으로 그리므로 `\n` 은 사라지고,
 * `<br>` 을 쓰려면 매장명(사용자 입력)을 HTML 로 넣어야 한다. 한 문장으로 이어 붙인다.
 */
export function applyConfirmMessage(view) {
  const storeNm = (view && view.storeNm) || ''
  const bizNo = (view && view.bizNo) || ''
  const callbackTel = (view && view.callbackTel) || PARTNER_LINK_TEXT.applyCallbackTelMissing
  const head = `${storeNm} (사업자번호 ${bizNo}) · ${PARTNER_LINK_TEXT.applyCallbackTelLabel} ${callbackTel}`
  return `${head} — ${PARTNER_LINK_TEXT.applyConfirmTail}`
}

/**
 * 해지 확인 문안 (네이버 §2.3 1단계) — 무엇을 해지하는지(매장 · 플레이스 ID)와 무엇이 멈추는지를 함께 말한다.
 * `$confirm` 한 문단 제약은 [applyConfirmMessage] 와 같다.
 */
export function unlinkConfirmMessage(view, link) {
  const storeNm = (view && view.storeNm) || ''
  const placeId = (link && link.externalCode) || ''
  const head = placeId ? `${storeNm} · ${PARTNER_LINK_TEXT.placeIdLabel} ${placeId}` : storeNm
  return head ? `${head} — ${PARTNER_LINK_TEXT.unlinkConfirmTail}` : PARTNER_LINK_TEXT.unlinkConfirmTail
}

/**
 * 참여 등록·해지 확인 문안 — 어느 매장의 일인지와 무엇이 바뀌는지를 함께 말한다.
 * `$confirm`·`$prompt` 한 문단 제약은 [applyConfirmMessage] 와 같다(줄바꿈이 사라진다).
 */
function participationConfirmMessage(view, tail) {
  const storeNm = (view && view.storeNm) || ''
  return storeNm ? `${storeNm} — ${tail}` : tail
}

export function joinConfirmMessage(view) {
  return participationConfirmMessage(view, PARTNER_LINK_TEXT.joinConfirmTail)
}

export function endConfirmMessage(view) {
  return participationConfirmMessage(view, PARTNER_LINK_TEXT.endConfirmTail)
}

/** 「연동일 · 해지일」 칸 — 그 축을 쓰지 않는 채널(KT)은 `'-'` 다. 해지 사유는 해지일 옆에서만 말한다 */
export function partnerLinkedAtText(link) {
  if (!link) return '-'
  const parts = []
  if (link.linkedAt) parts.push(link.linkedAt)
  if (link.unlinkedAt) {
    const reason = unlinkReasonText(link.unlinkReason)
    parts.push(`${PARTNER_LINK_TEXT.unlinkedAtLabel} ${link.unlinkedAt}${reason ? ` (${reason})` : ''}`)
  }
  return parts.length > 0 ? parts.join(' · ') : '-'
}

/**
 * 「정보」 버튼의 본문 — 채널이 가진 사실만 적는다. KT 는 상태 부기·사업자번호 공통 문장·해지 절차이고,
 * 네이버는 플레이스 ID·연동일·해지일(사유)·동의 시각이다. 값이 없는 줄은 만들지 않는다.
 */
export function partnerInfoMessage(view, link) {
  const partner = link && link.partner
  const note = partnerLinkChip(link, view ? view.bizNoStatus : null).note

  if (partner === PARTNER.NAVER_PLACE) {
    const facts = []
    if (link.externalCode) facts.push(`${PARTNER_LINK_TEXT.placeIdLabel} ${link.externalCode}`)
    if (link.linkedAt) facts.push(`${PARTNER_LINK_TEXT.linkedAtLabel} ${link.linkedAt}`)
    if (link.unlinkedAt) {
      const reason = unlinkReasonText(link.unlinkReason)
      facts.push(`${PARTNER_LINK_TEXT.unlinkedAtLabel} ${link.unlinkedAt}${reason ? ` (${reason})` : ''}`)
    }
    if (link.consentAt) facts.push(`${PARTNER_LINK_TEXT.consentAtLabel} ${link.consentAt}`)
    const head = facts.join(' · ')
    return [head, note].filter((v) => !!v).join(' — ')
  }

  const lines = [note, sameBizNoNote(view ? view.sameBizNoStoreCount : null), PARTNER_LINK_TEXT.terminateGuide]
  return lines.filter((v) => !!v).join(' ')
}

/** 위저드 주소 (`?storeCd=`). `storeCd` 가 없으면 쿼리를 지어내지 않는다 — 그 창이 매장을 짐작하면 안 된다 */
export function naverPlaceWizardPath(storeCd) {
  if (storeCd == null || storeCd === '') return NAVER_PLACE_LINK_PATH
  return `${NAVER_PLACE_LINK_PATH}?storeCd=${encodeURIComponent(storeCd)}`
}

/**
 * 배치 조회용 storeCd 묶음 — 상한([PARTNER_LINK_BATCH_MAX])을 넘으면 쪼갠다. 순수 함수.
 *
 * 값이 없는 행(신규 등록 중)은 버린다 — 서버가 400 으로 요청 전체를 거절하면 목록 전체가 「—」가 된다.
 */
export function chunkStoreCds(storeCds, size = PARTNER_LINK_BATCH_MAX) {
  if (!Array.isArray(storeCds)) return []
  const clean = storeCds.filter((cd) => cd != null && cd !== '')
  const limit = size > 0 ? size : PARTNER_LINK_BATCH_MAX
  const chunks = []
  for (let i = 0; i < clean.length; i += limit) {
    chunks.push(clean.slice(i, i + limit))
  }
  return chunks
}
